import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Flame,
  Sparkles,
  ArrowRight,
  Target,
  CalendarCheck,
  Lock,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { getProfile } from "@/data/practiceData";
import { getChapters, getSubjects } from "@/data/curriculum";
import { KIND_META, taskAction } from "@/lib/plannerActions";
import { apiFetch, getToken } from "@/api";
import PageBlobs from "@/components/PageBlobs";
import UserAvatar from "@/components/UserAvatar";
import Paywall from "@/components/Paywall";

/* Abhyas 2.0 Dashboard — answers "What should I do now?" not "what are my stats".
   Data comes from ONE shared intelligence endpoint (/performance/summary) +
   the student's Study Planner. Store rich data, display simple insights. */

const card = "rounded-[1.5rem] border border-[#E3ECE6] bg-white";
const eyebrow = "text-xs font-bold uppercase tracking-[0.2em]";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [streak, setStreak] = useState(0);
  const [me, setMe] = useState(null);
  const [intel, setIntel] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const profile = getProfile();

  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/me")
      .then((u) => {
        setMe(u);
        setStreak(u.currentStreak ?? 0);
      })
      .catch(() => {});
    apiFetch("/performance/summary")
      .then(setIntel)
      .catch(() => {})
      .finally(() => setLoading(false));
    apiFetch("/study-planner")
      .then((d) => setPlan(d.plan))
      .catch(() => {});
    apiFetch("/prompt-ai/credits")
      .then(setCredits)
      .catch(() => {});
  }, []);

  const name = me?.name || profile.name;
  const klass = me?.klass || profile.klass;
  const board = me?.board || profile.board;
  // Subject overview covers ALL subjects valid for the student's Board/Class
  // (subjects are a per-feature choice, never profile-hardcoded)
  const boardKey = board === "CBSE" ? "CBSE" : "CISCE";
  const curriculumSubjects = board && klass ? getSubjects(boardKey, String(klass)) : [];
  const subjects = curriculumSubjects.length
    ? curriculumSubjects
    : me?.subjects?.length > 0
      ? me.subjects
      : profile.subjects;
  const suggestedSubject = subjects[0] || "Mathematics";
  const [firstChapter] = getChapters(board, klass, suggestedSubject);

  const weakAreas = intel?.weakAreas || [];
  const topWeak = weakAreas[0] || null;
  const subjectRows = intel?.subjects || [];
  const recent = intel?.recent || [];
  const mistakeCount = intel?.mistakeCount || 0;

  const todayPlan = plan?.days?.find((d) => d.date === plan.today) ||
    plan?.days?.find((d) => d.status === "today");
  const todayTasks = todayPlan?.tasks || [];
  const nextTask = todayTasks.find((t) => !t.done);

  // Today's Focus: weakest area wins; then next planner task; then syllabus start
  const focus = topWeak
    ? {
        title: `Strengthen ${topWeak.chapter}`,
        reason: `${Math.round((topWeak.accuracy || 0) * 100)}% correct across ${topWeak.attempts} questions — your biggest opportunity right now.`,
        subject: topWeak.subject,
        action: () => navigate("/smart-practice"),
        cta: "Start Practice",
        testId: "todays-focus-start-btn",
      }
    : nextTask
      ? {
          title: nextTask.title,
          reason: `From your study plan for ${plan.examTarget}.`,
          subject: nextTask.subject,
          action: () =>
            nextTask.kind === "watch"
              ? navigate("/learn/videos")
              : navigate("/smart-practice"),
          cta: "Start Practice",
          testId: "todays-focus-start-btn",
        }
      : {
          title: `Start with ${firstChapter || "your first chapter"}`,
          reason: "One 5-question session is all it takes to personalize your practice.",
          subject: suggestedSubject,
          action: () => navigate("/practice-setup", { state: { subject: suggestedSubject } }),
          cta: "Start Practice",
          testId: "todays-focus-start-btn",
        };

  const contextLine = topWeak
    ? `Today is a good day to fix ${topWeak.chapter}.`
    : mistakeCount === 0 && (intel?.totals?.questions || 0) > 0
      ? "Clean sheet lately — keep the momentum going."
      : "A 10-minute session keeps your streak alive.";

  const quickStartChapter = (w) =>
    navigate("/practice-setup", {
      state: {
        quickStart: {
          board: board === "CBSE" ? "CBSE" : "CISCE",
          klass: String(klass),
          subject: w.subject,
          chapter: w.chapter,
          difficulty: "Medium",
        },
      },
    });

  const toggleTask = async (task) => {
    const done = !task.done;
    setPlan((p) => ({
      ...p,
      days: p.days.map((d) => ({
        ...d,
        tasks: d.tasks.map((t) => (t.id === task.id ? { ...t, done } : t)),
      })),
    }));
    try {
      await apiFetch("/study-planner/task-toggle", { method: "POST", body: { taskId: task.id, done } });
    } catch {
      /* non-blocking */
    }
  };

  return (
    <div
      data-testid="dashboard-page"
      className="relative min-h-screen overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <header className="relative z-40 border-b border-[#E3ECE6] bg-[#F8FAF7]/85 backdrop-blur-md">
        <div className="mx-auto flex w-[94%] max-w-[1520px] items-center justify-between py-5">
          <Link to="/" data-testid="logo-home-link" className="shrink-0 font-display text-2xl font-extrabold">
            <img src="/logo.png" alt="Abhyas" className="h-9 w-auto sm:h-10" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="upgrade-btn"
              onClick={() => setPaywallOpen(true)}
              className="rounded-full bg-[#FACC15] px-3 py-1.5 text-xs font-bold text-[#17201A] transition-[background-color,transform] duration-200 hover:bg-[#FACC15]/80 sm:px-5 sm:py-2 sm:text-sm"
            >
              <span className="sm:hidden">Premium</span>
              <span className="hidden sm:inline">Go Premium</span>
            </button>
            {me && (
              <span
                data-testid="streak-badge"
                className="animate-pulse-soft inline-flex items-center gap-1.5 rounded-full bg-[#FACC15]/25 px-2.5 py-1.5 text-xs font-bold text-[#17201A] sm:px-4 sm:py-2 sm:text-sm"
              >
                <Flame className="h-3.5 w-3.5 text-[#FACC15] sm:h-4 sm:w-4" />
                <span className="sm:hidden">{streak}</span>
                <span className="hidden sm:inline">{streak}-day streak</span>
              </span>
            )}
            <UserAvatar />
          </div>
        </div>
      </header>
      <Paywall open={paywallOpen} onClose={() => setPaywallOpen(false)} />

      <main className="relative mx-auto w-[94%] max-w-[1520px] pb-20">
        {/* Welcome — personalized, contextual */}
        <div className="mt-10 animate-fade-up">
          <h1 data-testid="dashboard-greeting" className="font-display text-3xl font-extrabold sm:text-4xl">
            {greeting()}, <span className="italic text-[#16A34A]">{name}</span>
          </h1>
          <p data-testid="dashboard-context-line" className="mt-2 text-sm font-semibold text-[#647067]">
            Grade {klass} · {board} — {contextLine}
          </p>
        </div>

        {/* How you're doing — quick activity snapshot; the next action stays secondary */}
        <div
          data-testid="todays-focus-card"
          className={`${card} animate-fade-up mt-8 p-8 sm:p-10`}
          style={{ animationDelay: "120ms" }}
        >
          <p className={`${eyebrow} inline-flex items-center gap-1.5 text-[#B45309]`}>
            <Sparkles className="h-3.5 w-3.5 text-[#FACC15]" />
            How you're doing
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
            {[
              {
                label: "Questions practiced",
                value: intel?.totals?.questions ?? 0,
                testId: "focus-stat-questions",
              },
              {
                label: "Practice sessions",
                value: intel?.totals?.sessions ?? 0,
                testId: "focus-stat-sessions",
              },
              {
                label: "Day streak",
                value: streak,
                testId: "focus-stat-streak",
              },
              {
                label: "Accuracy",
                value:
                  intel?.totals?.accuracy != null
                    ? `${Math.round(intel.totals.accuracy * 100)}%`
                    : "—",
                testId: "focus-stat-accuracy",
              },
            ].map((s) => (
              <div
                key={s.testId}
                data-testid={s.testId}
                className="rounded-2xl border border-[#E3ECE6] bg-[#F8FAF7] px-4 py-4"
              >
                <p className="font-display text-2xl font-extrabold text-[#17201A] sm:text-3xl">
                  {s.value}
                </p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.1em] text-[#647067]">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-[#E3ECE6] pt-5">
            <p data-testid="todays-focus-title" className="text-sm font-semibold text-[#647067]">
              Up next: <span className="font-extrabold text-[#17201A]">{focus.title}</span>
              <span className="hidden sm:inline"> — {focus.subject}</span>
            </p>
            <button
              data-testid={focus.testId}
              onClick={focus.action}
              className="group ml-auto inline-flex items-center gap-1.5 rounded-full border-2 border-[#16A34A] px-4 py-1.5 text-xs font-bold text-[#16A34A] transition-colors duration-200 hover:bg-[#16A34A] hover:text-white"
            >
              {focus.cta}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Main column */}
          <div className="space-y-6 lg:col-span-2">
            {/* Today's Plan — from the student's Study Planner */}
            <div
              data-testid="todays-plan-card"
              className={`${card} animate-fade-up p-7 sm:p-8`}
              style={{ animationDelay: "200ms" }}
            >
              <div className="flex items-center justify-between">
                <p className={`${eyebrow} inline-flex items-center gap-1.5 text-[#647067]`}>
                  <CalendarCheck className="h-3.5 w-3.5 text-[#16A34A]" />
                  Today's Plan
                </p>
                {plan && (
                  <Link
                    to="/planner"
                    data-testid="view-full-plan-link"
                    className="text-xs font-bold text-[#16A34A] underline-offset-2 hover:underline"
                  >
                    Full week
                  </Link>
                )}
              </div>
              {plan ? (
                plan.examToday ? (
                  <p data-testid="todays-plan-exam-day" className="mt-3 flex items-center gap-2 rounded-xl bg-[#14532D]/5 px-4 py-3.5 text-sm font-bold text-[#14532D]">
                    Exam day — your preparation plan is complete. All the best!
                  </p>
                ) : (
                <div data-testid="todays-plan-list" className="mt-3 divide-y divide-[#E3ECE6]">
                  {todayTasks.map((t) => {
                    const Icon = (KIND_META[t.kind] || KIND_META.revise).icon;
                    const action = taskAction(t, me);
                    return (
                      <div key={t.id} data-testid={`dashboard-task-${t.id}`} className="flex items-center gap-3 py-3">
                        <button
                          data-testid={`dashboard-task-toggle-${t.id}`}
                          aria-label={t.done ? "Mark not done" : "Mark done"}
                          onClick={() => toggleTask(t)}
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ${
                            t.done ? "border-[#16A34A] bg-[#16A34A]" : "border-[#E2E8F0] bg-white hover:border-[#16A34A]"
                          }`}
                        >
                          {t.done && <CheckCircle2 className="h-4 w-4 text-white" />}
                        </button>
                        <Icon className="h-4 w-4 shrink-0 text-[#16A34A]" />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm font-bold ${t.done ? "text-[#94A39B] line-through" : ""}`}>
                            {t.title}
                          </p>
                          <p className="text-xs font-semibold text-[#647067]">
                            {t.subject} · {t.minutes} min
                          </p>
                        </div>
                        {action && !t.done && (
                          <button
                            data-testid={`dashboard-task-action-${t.id}`}
                            onClick={() => navigate(action.to, action.state ? { state: action.state } : undefined)}
                            className="shrink-0 rounded-full border border-[#16A34A]/30 px-3 py-1 text-[11px] font-bold text-[#16A34A] transition-colors duration-200 hover:bg-[#16A34A] hover:text-white"
                          >
                            Start →
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {todayTasks.length === 0 && (
                    <p className="py-4 text-sm font-semibold text-[#647067]">
                      Nothing scheduled for today — enjoy the breather.
                    </p>
                  )}
                </div>
                )
              ) : (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
                  <p data-testid="todays-plan-empty" className="max-w-sm text-sm font-semibold text-[#647067]">
                    No study plan yet. Tell Abhyas your exam date and subjects
                    — it sequences coverage, practice and revision across the
                    days you actually have.
                  </p>
                  <button
                    data-testid="create-plan-cta"
                    onClick={() => navigate("/planner")}
                    className="inline-flex items-center gap-2 rounded-full border border-[#16A34A] px-5 py-2.5 text-xs font-bold text-[#16A34A] transition-[background-color,color] duration-200 hover:bg-[#16A34A] hover:text-white"
                  >
                    Build my plan
                  </button>
                </div>
              )}
            </div>

            {/* Weak Areas — only the most useful few */}
            {weakAreas.length > 0 && (
              <div
                data-testid="weak-areas-card"
                className={`${card} animate-fade-up p-7 sm:p-8`}
                style={{ animationDelay: "260ms" }}
              >
                <p className={`${eyebrow} inline-flex items-center gap-1.5 text-[#647067]`}>
                  <AlertTriangle className="h-3.5 w-3.5 text-[#FACC15]" />
                  Needs Attention
                </p>
                <div className="mt-4 space-y-3">
                  {weakAreas.slice(0, 3).map((w, i) => (
                    <div
                      key={`${w.subject}-${w.chapter}`}
                      data-testid={`weak-area-${i}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#F8FAF7] px-5 py-4"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-display text-base font-bold">{w.chapter}</p>
                        <p className="text-xs font-semibold text-[#647067]">
                          {w.subject} · {Math.round((w.accuracy || 0) * 100)}% correct · {w.wrong} missed
                        </p>
                      </div>
                      <button
                        data-testid={`weak-area-practice-${i}`}
                        onClick={() => quickStartChapter(w)}
                        className="shrink-0 rounded-full bg-[#16A34A] px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#15803D]"
                      >
                        Practice
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent activity */}
            <div className="animate-fade-up" style={{ animationDelay: "320ms" }}>
              <h2 className="font-display text-xl font-extrabold sm:text-2xl">Recent activity</h2>
              <div data-testid="recent-practice-list" className={`${card} mt-4 divide-y divide-[#E3ECE6] px-6`}>
                {recent.length === 0 && (
                  <p data-testid="recent-empty" className="py-6 text-sm font-semibold text-[#647067]">
                    No sessions yet — finish your first practice to see it here.
                  </p>
                )}
                {recent.slice(0, 4).map((s, i) => (
                  <div key={s.id || i} data-testid={`recent-session-${i}`} className="flex items-center justify-between gap-4 py-5">
                    <div>
                      <p className="font-display text-base font-bold">{s.chapter}</p>
                      <p className="mt-0.5 text-xs font-semibold text-[#647067]">
                        {s.subject} · {formatDate(s.completedAt)}
                      </p>
                    </div>
                    <span className="rounded-full bg-[#16A34A]/10 px-4 py-1.5 text-xs font-bold text-[#16A34A]">
                      {s.score !== null && s.score !== undefined ? `${s.score}/${s.outOf}` : `${s.questionsCompleted} Q`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Side column */}
          <div className="space-y-6">
            {/* Smart Practice — the intelligent recommendation */}
            <div
              data-testid="smart-practice-card"
              className={`${card} animate-fade-up p-6`}
              style={{ animationDelay: "200ms" }}
            >
              <p className={`${eyebrow} inline-flex items-center gap-1.5 text-[#B45309]`}>
                <Target className="h-3.5 w-3.5 text-[#FACC15]" />
                Smart Practice
              </p>
              <p data-testid="smart-practice-text" className="mt-3 font-display text-lg font-bold leading-snug">
                {topWeak
                  ? "5 questions recommended based on your recent performance."
                  : "Answer a few questions and Smart Practice starts targeting what you need most."}
              </p>
              <button
                data-testid="smart-practice-start-btn"
                onClick={() => navigate("/smart-practice")}
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#16A34A] px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#15803D]"
              >
                Start Smart Practice
              </button>
              {mistakeCount > 0 && (
                <button
                  data-testid="reattempt-link"
                  onClick={() => navigate("/reattempt")}
                  className="mt-3 block text-xs font-bold text-[#16A34A] underline-offset-2 hover:underline"
                >
                  Reattempt {mistakeCount} missed question{mistakeCount === 1 ? "" : "s"} →
                </button>
              )}
            </div>

            {/* Subject performance — simple overview */}
            <div
              data-testid="subject-performance-card"
              className={`${card} animate-fade-up p-6`}
              style={{ animationDelay: "260ms" }}
            >
              <div className="flex items-center justify-between">
                <p className={`${eyebrow} text-[#647067]`}>Subject Performance</p>
                <Link
                  to="/progress"
                  data-testid="view-progress-link"
                  className="text-xs font-bold text-[#16A34A] underline-offset-2 hover:underline"
                >
                  Progress
                </Link>
              </div>
              <div className="mt-4 space-y-4">
                {subjects.map((s) => {
                  const row = subjectRows.find((r) => r.subject === s);
                  const acc = row?.accuracy;
                  return (
                    <div key={s} data-testid={`subject-row-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-bold">{s}</span>
                        <span className="text-xs font-bold text-[#647067]">
                          {acc !== null && acc !== undefined
                            ? `${Math.round(acc * 100)}% · ${row.practiced} Q`
                            : "Not started"}
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[#17201A]/8">
                        <div
                          className="h-full rounded-full bg-[#16A34A] transition-[width] duration-700"
                          style={{ width: `${acc ? Math.round(acc * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ask Abhyas — credits strip (kept slim) */}
            {credits && (
              <div
                data-testid="prompt-credits-card"
                className={`${card} animate-fade-up flex items-center justify-between gap-4 p-5`}
                style={{ animationDelay: "320ms" }}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                      credits.plan === "Free" && !credits.weekPassActive ? "bg-[#94A39B]/10" : "bg-[#16A34A]/10"
                    }`}
                  >
                    {credits.plan === "Free" && !credits.weekPassActive ? (
                      <Lock className="h-4.5 w-4.5 text-[#94A39B]" />
                    ) : (
                      <Sparkles className="h-4.5 w-4.5 text-[#16A34A]" />
                    )}
                  </span>
                  <div>
                    <p data-testid="prompt-credits-balance" className="text-sm font-extrabold text-[#17201A]">
                      {credits.plan === "Free" && !credits.weekPassActive
                        ? "Ask Abhyas isn't included in Free"
                        : `${credits.balance} AI credits`}
                    </p>
                    <p className="text-xs font-semibold text-[#647067]">
                      {credits.weekPassActive
                        ? "7-Day Pass active"
                        : credits.plan === "Free"
                          ? "Upgrade to chat with Abhyas AI"
                          : `${credits.plan} plan`}
                    </p>
                  </div>
                </div>
                <button
                  data-testid="open-abhyas-ai"
                  onClick={() => navigate("/abhyas-ai")}
                  className="shrink-0 rounded-full bg-[#16A34A] px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#15803D]"
                >
                  Ask
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      {loading && (
        <div data-testid="dashboard-loading" className="pointer-events-none fixed bottom-5 right-5">
          <Sparkles className="h-5 w-5 animate-pulse text-[#16A34A]" />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
